import { useEffect, useState } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Home, Package, ShoppingCart, User } from 'lucide-react'
import { formatNumber } from '@najah/core'
import { FullPageSpinner, LogoMark, ThemeProvider, ToastProvider, cn } from '@najah/ui'
import { AuthProvider, useAuth } from './lib/auth'
import { CartProvider, useCart } from './lib/cart'
import { HomeScreen } from './screens/HomeScreen'
import { ProductScreen } from './screens/ProductScreen'
import { CartScreen } from './screens/CartScreen'
import { OrdersScreen } from './screens/OrdersScreen'
import { AccountScreen } from './screens/AccountScreen'
import { App as NativeApp } from '@capacitor/app'
import { connectPush, consumePushOrder, supportsPush } from './lib/pushClient'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        const status = (error as { status?: number }).status
        if (status && status >= 400 && status < 500) return false
        return failureCount < 2
      },
    },
  },
})

type Tab = 'home' | 'orders' | 'cart' | 'account'

const TABS: { key: Tab; label: string; icon: typeof Home }[] = [
  { key: 'home', label: 'الرئيسية', icon: Home },
  { key: 'orders', label: 'طلباتي', icon: Package },
  { key: 'cart', label: 'السلة', icon: ShoppingCart },
  { key: 'account', label: 'حسابي', icon: User },
]

function Shell() {
  const { customer, isRestoring } = useAuth()
  const cart = useCart()
  const [tab, setTab] = useState<Tab>('home')
  const [productId, setProductId] = useState<string | null>(null)
  const [notificationOrderId, setNotificationOrderId] = useState<string | null>(null)

  useEffect(() => {
    setNotificationOrderId(null)
    void connectPush(customer?.id ?? null)
    const refresh = () => { void queryClient.invalidateQueries({ queryKey: ['my-orders'] }) }
    const openOrder = () => {
      if (!customer) return
      const id = consumePushOrder(customer.id)
      if (!id) return
      refresh()
      setNotificationOrderId(id)
      setProductId(null)
      setTab('orders')
    }
    const reconnect = () => { void connectPush(customer?.id ?? null); refresh() }
    window.addEventListener('najah:order-update', refresh)
    window.addEventListener('najah:open-order', openOrder)
    window.addEventListener('online', reconnect)
    const nativeListener = supportsPush ? NativeApp.addListener('appStateChange', ({ isActive }) => { if (isActive) reconnect() }) : null
    openOrder()
    return () => {
      window.removeEventListener('najah:order-update', refresh)
      window.removeEventListener('najah:open-order', openOrder)
      window.removeEventListener('online', reconnect)
      void nativeListener?.then((listener) => listener.remove())
    }
  }, [customer?.id])

  // زر الرجوع في أندرويد يُغلق صفحة المنتج بدل الخروج من التطبيق
  useEffect(() => {
    if (!productId) return
    const onPop = () => setProductId(null)
    window.history.pushState({ product: productId }, '')
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [productId])

  if (isRestoring) return <FullPageSpinner label="جارٍ التحميل…" />

  if (productId) {
    return (
      <div className="min-h-dvh bg-bg">
        <ProductScreen productId={productId} onBack={() => setProductId(null)} />
        <TabBar tab={tab} setTab={(t) => { setProductId(null); setTab(t) }} cartCount={cart.count} />
      </div>
    )
  }

  return (
    <div className="flex min-h-dvh flex-col bg-bg">
      <header className="sticky top-0 z-10 flex h-14 items-center gap-2.5 border-b border-border bg-surface/90 px-4 backdrop-blur-md">
        <LogoMark variant="store" className="size-8" />
        <p className="font-display text-base font-bold text-fg">مكتبة النجاح</p>
      </header>

      <main className="flex-1 pb-20">
        {tab === 'home' && <HomeScreen onOpenProduct={setProductId} />}
        {tab === 'orders' && <OrdersScreen notificationOrderId={notificationOrderId} />}
        {tab === 'cart' && <CartScreen onOrdered={() => setTab('orders')} />}
        {tab === 'account' && <AccountScreen onOpenProduct={setProductId} />}
      </main>

      <TabBar tab={tab} setTab={setTab} cartCount={cart.count} />
    </div>
  )
}

function TabBar({ tab, setTab, cartCount }: { tab: Tab; setTab: (t: Tab) => void; cartCount: number }) {
  return (
    <nav className="fixed inset-x-0 bottom-0 z-20 grid grid-cols-4 border-t border-border bg-surface pb-[env(safe-area-inset-bottom)]">
      {TABS.map(({ key, label, icon: Icon }) => (
        <button
          key={key}
          type="button"
          onClick={() => setTab(key)}
          className={cn(
            'relative flex flex-col items-center gap-1 py-2.5 text-[11px] transition-colors',
            tab === key ? 'text-primary' : 'text-fg-subtle',
          )}
          aria-current={tab === key ? 'page' : undefined}
        >
          <span className="relative">
            <Icon className="size-5" />
            {key === 'cart' && cartCount > 0 && (
              <span
                className="absolute -top-1.5 -end-2 grid min-w-4 place-items-center rounded-full bg-danger px-1 text-[10px] font-bold text-white"
                dir="ltr"
              >
                {formatNumber(cartCount)}
              </span>
            )}
          </span>
          {label}
        </button>
      ))}
    </nav>
  )
}

export function App() {
  return (
    <ThemeProvider>
      <ToastProvider>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <CartProvider>
              <Shell />
            </CartProvider>
          </AuthProvider>
        </QueryClientProvider>
      </ToastProvider>
    </ThemeProvider>
  )
}
